import { RecordUtils } from "@rsm-hcd/javascript-core";
import {
    NotebookPageContentBlock,
    NotebookPageContentBlockCultureCode,
    NotebookPageContentBlockIcon,
} from "constants/notebooks/notebook-page-content-blocks";
import { ChecklistBlockConfigurationRecord } from "models/view-models/block-configurations/checklist-block-configuration-record";
import { CodeReferenceBlockConfigurationRecord } from "models/view-models/block-configurations/code-reference-block-configuration-record";
import { TextBlockConfigurationRecord } from "models/view-models/block-configurations/text-block-configuration-record";
import { BlockRecord } from "models/view-models/block-record";
import ConversationApplicationContextRecord from "models/view-models/conversations/conversation-application-context-record";
import { useCallback, useMemo } from "react";
import useCodeReferenceBlockSectionApi from "utilities/hooks/domain/block-configurations/use-code-reference-block-section-api";
import useCurrentPage from "utilities/hooks/domain/notebooks/pages/use-current-page";
import { usePageBlockState } from "utilities/hooks/domain/notebooks/pages/use-page-block-state";
import useCurrentNotebook from "utilities/hooks/domain/notebooks/use-current-notebook";
import { t } from "utilities/localization-utils";
import StringUtils from "utilities/string-utils";

interface NotebookChatContext {
    notebookName: string | null;
    pageName: string | null;
    block: {
        icon: (typeof NotebookPageContentBlockIcon)[keyof typeof NotebookPageContentBlockIcon];
        label: string | null;
    } | null;
}

export default function useNotebookChatContext() {
    const { currentNotebook } = useCurrentNotebook();
    const { currentPage } = useCurrentPage();
    const { activeBlock } = usePageBlockState();

    const sectionId = useMemo(() => {
        if (activeBlock?.type !== NotebookPageContentBlock.CodeReference) {
            return undefined;
        }

        const config = RecordUtils.ensureRecord(
            activeBlock.configuration,
            CodeReferenceBlockConfigurationRecord
        );

        return config.sectionId;
    }, [activeBlock?.configuration, activeBlock?.type]);

    // fetches from the react-query cache if already fetched
    const { section } = useCodeReferenceBlockSectionApi(sectionId);

    const getBlockSnippet = useCallback(
        (block?: BlockRecord) => {
            if (block?.type == null) return null;

            const fallbackLabel = getFallbackBlockTypeLabel(block.type);

            try {
                switch (block.type) {
                    case NotebookPageContentBlock.Text:
                        const textConfig = new TextBlockConfigurationRecord(
                            block.configuration
                        );
                        const textSnippet = normalizeSnippet(
                            StringUtils.stripHtmlTags(textConfig.content)
                        );
                        return textSnippet ?? fallbackLabel;

                    case NotebookPageContentBlock.Checklist:
                        const checklistConfig =
                            new ChecklistBlockConfigurationRecord(
                                block.configuration
                            );
                        const checkSnippet = normalizeSnippet(
                            checklistConfig.items?.at(0)?.text
                        );
                        return checkSnippet ?? fallbackLabel;

                    case NotebookPageContentBlock.CodeReference:
                        const codeRefSnippet = normalizeSnippet(
                            section?.getTitleAsPlainText() ??
                                section?.bodyAsPlainText
                        );
                        return codeRefSnippet ?? fallbackLabel;

                    case NotebookPageContentBlock.Image:
                    default:
                        return fallbackLabel;
                }
            } catch {
                return fallbackLabel;
            }
        },
        [section]
    );

    const notebookChatContext = useMemo(() => {
        const context: NotebookChatContext = {
            notebookName: null,
            pageName: null,
            block: null,
        };

        if (currentNotebook?.name != null)
            context.notebookName = currentNotebook.name;
        if (currentPage?.name != null) context.pageName = currentPage.name;

        if (activeBlock?.type != null) {
            const icon = NotebookPageContentBlockIcon[activeBlock.type];
            const label = getBlockSnippet(activeBlock);

            context.block = { icon, label };
        }

        return context;
    }, [
        activeBlock,
        currentNotebook?.name,
        currentPage?.name,
        getBlockSnippet,
    ]);

    const notebookApplicationContext = useMemo(() => {
        const applicationContext =
            new ConversationApplicationContextRecord().buildNotebookContext(
                currentNotebook ?? undefined,
                currentPage ?? undefined,
                activeBlock,
                getBlockSnippet(activeBlock) ?? undefined
            );

        return applicationContext;
    }, [activeBlock, currentNotebook, currentPage, getBlockSnippet]);

    return { notebookChatContext, notebookApplicationContext };
}

const BLOCK_SNIPPET_LENGTH = 24;

const normalizeSnippet = (value?: string) => {
    // replace multiple whitespace characters with a single space
    const normalized = (value ?? "").replace(/\s+/g, " ").trim();
    const label = normalized.slice(0, BLOCK_SNIPPET_LENGTH);
    return label.length > 0 ? label : null;
};

const getFallbackBlockTypeLabel = (type: NotebookPageContentBlock) =>
    t(NotebookPageContentBlockCultureCode[type]);
